import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import type { SelectOption } from '@core/user-interface-legacy';
import { SelectComponent, SpinnerComponent } from '@core/user-interface-legacy';

import { Destroyable, takeUntilDestroyed } from '../../../common';
import type { DynamicResourceSelectInputConfig } from '../../types';
import { DynamicFormInput } from '../dynamic-form/dynamic-form-input/dynamic-form-input';
import type { Resource } from '../dynamic-form/dynamic-form-resources.service';
import { DynamicFormResourcesService } from '../dynamic-form/dynamic-form-resources.service';

export class ResourceSelectInput extends DynamicFormInput<DynamicResourceSelectInputConfig> {
  public get includeEmptyOption(): boolean {
    if (this.config.multiple) {
      return false;
    } else if (this.config.required) {
      return false;
    } else {
      return true;
    }
  }
  public get multiple(): boolean {
    return !!this.config.multiple;
  }
}

@Component({
  selector: 'rti-resource-select-input',
  templateUrl: './resource-select-input.component.html',
  styleUrls: ['./resource-select-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, SpinnerComponent, SelectComponent],
})
@Destroyable()
export class ResourceSelectInputComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly resourcesSvc = inject(DynamicFormResourcesService);
  private readonly i18n = inject(I18nService);

  private readonly SEARCH_THRESHOLD: number = 7;

  public readonly input = input<ResourceSelectInput>(undefined!);

  public error: string | null = null;
  public loading: boolean = false;

  private resources: Resource[] | null = null;

  public ngOnInit(): void {
    this.resourcesSvc
      .get(this.input().config.resource)
      .pipe(takeUntilDestroyed(this))
      .subscribe(resources => {
        if ('error' in resources) {
          this.error = resources.error;
          this.resources = null;
        } else {
          this.resources = resources;
          this.error = null;
        }

        this.ensureInputValueWithinResources();
        this.maybePreselectInputValue();
        this.cdRef.detectChanges();
      });

    this.resourcesSvc.loading$.pipe(takeUntilDestroyed(this)).subscribe(loading => {
      this.loading = loading;
      this.cdRef.detectChanges();
    });
  }

  public ngOnChanges(): void {
    const input = this.input();
    this.showSpinner || (this.loading && !!input.config.disableWhenLoading)
      ? input.control.disable()
      : input.control.enable();
  }

  public get resourceSelectOptions(): Array<SelectOption<any>> {
    return this.resources || [];
  }

  public get selectPlaceholder(): string | undefined {
    return this.resources ? undefined : this.i18n.translate('rti.common.loading');
  }

  public showSearch(): boolean {
    const input = this.input();
    if ('hasSearch' in input.config) {
      return !!input.config.hasSearch;
    } else {
      return (this.resources || []).length >= this.SEARCH_THRESHOLD;
    }
  }

  public get showSpinner(): boolean {
    return !!this.input().config.loadOnSelect && this.valuePresent();
  }

  private valuePresent(): boolean {
    const value = this.input().control.value;
    return (this.input().multiple && value?.length > 0) || !!value;
  }

  private ensureInputValueWithinResources(): void {
    if (this.input().multiple) {
      this.ensureMultipleValuesWithinResources();
    } else {
      this.ensureValueWithinResources();
    }
  }

  private ensureMultipleValuesWithinResources(): void {
    const values = (this.resources || [])
      .filter(resource => this.isSelected(resource, this.input().control.value || []))
      .map(resource => resource.value);

    this.input().control.setValue(values);
  }

  private ensureValueWithinResources(): void {
    const resource = (this.resources || []).find(resource => {
      const input = this.input();
      return this.isSelected(resource, input.control.value ? [input.control.value] : []);
    });

    this.input().control.setValue(resource?.value);
  }

  private maybePreselectInputValue(): void {
    if (this.valuePresent()) {
      return;
    }

    const resources = this.resources || [];

    if (this.input().multiple) {
      this.maybePreselectMultipleValues(resources);
    } else {
      this.maybePreselectValue(resources);
    }
  }

  private maybePreselectValue(resources: Resource[]): void {
    this.input().control.setValue(resources.length === 1 ? resources[0].value : null);
  }

  private maybePreselectMultipleValues(resources: Resource[]): void {
    this.input().control.setValue(resources.length === 1 ? [resources[0].value] : []);
  }

  private isSelected(resource: Resource, values: Array<string | number | null>): boolean {
    const comparableValues = values.filter(value => !!value).map(this.comparableValue);

    return (
      comparableValues.includes(this.comparableValue(resource.value)) ||
      comparableValues.includes(this.comparableValue(resource.label))
    );
  }

  private comparableValue(value: string | number): string {
    return value.toString().toLowerCase();
  }
}
